<template>
  <v-container>
    <v-row>
      <v-col>
        <h1>Verträge</h1>
      </v-col>
    </v-row>
    <v-row>
      <v-data-table
        :headers="headers"
        :items="contractCustomerData"
        sort-by="created_at"
        :sort-desc="true"
      >
        <template #item.organization_name="{ item }">
          <router-link :to="{ name: 'organization.dashboard', params: { id: item.organizationId } }">
            {{ item.organization_name }}
          </router-link>
        </template>
        <template #item.created_at="{ item }">
          {{ momentize(item.created_at) }}
        </template>
        <template #item._offer_devices="{ item }">
          <div class="d-flex flex-row align-center grid-gap">
            <div class="related-devices">
              <div
                v-for="(device, index) in item.requestedDeviceProducts"
                :key="index"
              >
                <device-icon :device-class="device.deviceClass"></device-icon>
                {{ device.count }}
              </div>
            </div>
            <span
              v-if="item.relatedFileDevices"
              class="related-file-name"
            >
              {{ item.relatedFileDevices.name.replace('Anlage 7 - ', '') }}
            </span>
            <div v-else>
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-on="on"
                    v-bind="attrs"
                    icon
                    @click="initializeAttachmentUpload(item, RELATED_FILE_ATTACHMENT_IDENTIFIER_DEVICES)"
                  >
                    <v-icon>{{ mdiUpload }}</v-icon>
                  </v-btn>
                </template>
                <span>Anlage für Geräte hochladen</span>
              </v-tooltip>
            </div>
          </div>
        </template>
        <template #item.relatedFileEcom="{ item }">
          <div class="d-flex flex-row grid-gap">
            <div class="related-devices">
              <div
                v-for="(device, index) in item.requestedEcomProducts"
                :key="index"
              >
                <device-icon :device-class="device.deviceClass"></device-icon>
                {{ device.count }}
              </div>
            </div>
            <span
              v-if="item.relatedFileEcom"
              class="related-file-name"
            >
              {{ item.relatedFileEcom.name.replace('Anlage 8 - ', '') }}
            </span>
            <div v-else>
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-on="on"
                    v-bind="attrs"
                    icon
                    @click="initializeAttachmentUpload(item, RELATED_FILE_ATTACHMENT_IDENTIFIER_ECOM)"
                  >
                    <v-icon>{{ mdiUpload }}</v-icon>
                  </v-btn>
                </template>
                <span>Anlage für Ecommerce hochladen</span>
              </v-tooltip>
            </div>
          </div>
        </template>
        <template #item._person1="{ item }">
          {{ item.relevantPeople[0] ?? '' }}
        </template>
        <template #item._person2="{ item }">
          {{ item.relevantPeople[1] ?? '' }}
        </template>
        <template #item._person3="{ item }">
          {{ item.relevantPeople[2] ?? '' }}
        </template>
        <template #item._actions="{ item }">
          <div class="d-flex">
            <v-tooltip
              v-if="item.generatedContractBundle"
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  :disabled="item.isContractRegenerating"
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @click="regenerateContract(item)"
                >
                  <v-icon>{{ mdiReload }}</v-icon>
                </v-btn>
              </template>
              <span>{{ item.isContractRegenerating ? 'Vertrag wird neu erzeugt': 'Vertrag neu erzeugen' }}</span>
            </v-tooltip>
            <v-tooltip
              v-else
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  :disabled="item.isContractRegenerating"
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @click="generateContract(item)"
                >
                  <v-icon>{{ mdiFileDocumentPlusOutline }}</v-icon>
                </v-btn>
              </template>
              <span>{{ item.isContractRegenerating ? 'Vertrag wird erzeugt': 'Vertrag erzeugen' }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  :disabled="!item.generatedContractBundle"
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @click="downloadContract(item)"
                >
                  <v-icon>{{ mdiDownloadOutline }}</v-icon>
                </v-btn>
              </template>
              <span>{{ item.generatedContractBundle ? 'Vertrag herunterladen' : 'Kein Vertrag verfügbar' }}</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
      <v-file-input
        v-show="false"
        v-model="uploadFile"
        id="related-file-chooser"
        ref="relatedFileChooser"
        outlined
        accept="application/pdf"
        label="Datei auswählen"
      ></v-file-input>
    </v-row>
  </v-container>
</template>

<script>
import {
  FETCH_CONTACTS,
  FETCH_CONTRACT_CUSTOMER_DATA,
  FETCH_CONTRACT_PERSON_DATA,
  FETCH_CONTRACT_RELATED_FILES,
  FETCH_CONTRACTS,
  FETCH_GENERATED_CONTRACT_BUNDLES,
  FETCH_GLOBAL_DEVICES,
  FETCH_ORGANIZATIONS, GENERATE_CONTRACT,
  REGENERATE_CONTRACT, UPLOAD_CONTRACT_RELATED_FILE,
} from "@/store/action-types"
import { mapGetters, mapState } from "vuex"
import moment from "moment"
import {
  mdiReload,
  mdiDownloadOutline,
  mdiDownload,
  mdiFileDocumentPlusOutline,
  mdiUpload
} from "@mdi/js"
import DeviceIcon from "@/components/icons/DeviceIcon.vue"
import { downloadBlob } from "@/lib/file-tools"
import {
  RELATED_FILE_ATTACHMENT_IDENTIFIER_DEVICES,
  RELATED_FILE_ATTACHMENT_IDENTIFIER_ECOM
} from "@/store/contracts"

export default {
  name: 'AdminContracts',
  components: {
    DeviceIcon
  },
  data () {
    return {
      RELATED_FILE_ATTACHMENT_IDENTIFIER_DEVICES,
      RELATED_FILE_ATTACHMENT_IDENTIFIER_ECOM,
      mdiReload,
      mdiDownloadOutline,
      mdiFileDocumentPlusOutline,
      mdiUpload,
      activeUploadItem: null,
      activeUploadType: null,
      uploadFile: null,
    }
  },
  computed: {
    ...mapState('staticConfiguration', ['enumsByName']),
    ...mapGetters('organization', ['allOrganizations']),
    ...mapGetters('contracts', ['contractCustomerData', 'contractPersonDataByUrl']),
    headers () {
      return [
        {
          text: 'Organisation',
          value: 'organization_name',
        },
        {
          text: 'Erstellt am',
          value: 'created_at'
        },
        {
          text: 'Angebot Geräte',
          value: '_offer_devices'
        },
        {
          text: 'Angebot ECOM',
          value: 'relatedFileEcom'
        },
        {
          text: 'Person 1',
          value: '_person1'
        },
        {
          text: 'Person 2',
          value: '_person2'
        },
        {
          text: 'Person 3',
          value: '_person3'
        },
        {
          text: '',
          value: '_actions'
        }
      ]
    }
  },
  methods: {
    mdiDownload () {
      return mdiDownload
    },
    ...mapGetters('contracts', ['getContractPersonByUrl']),
    momentize (dateTime) {
      return moment(dateTime).format('DD.MM.YYYY')
    },
    async generateContract (customer) {
      await this.$store.dispatch('contracts/' + GENERATE_CONTRACT, { customerUrl: customer.url })
      customer.isContractRegenerating = true
    },
    async regenerateContract (customer) {
      await this.$store.dispatch('contracts/' + REGENERATE_CONTRACT, { customerUrl: customer.url })
      customer.isContractRegenerating = true
    },
    async downloadContract (item) {
      if (!item.generatedContractBundle) {
        return
      }

      try {
        const bundle = item.generatedContractBundle
        const response = await this.$store.getters.restApi.get(`${bundle.url}download/`, { responseType: 'blob' })
        downloadBlob(response.data, bundle.name, response.headers['content-type'])
      } catch (error) {
        this.$store.commit('error', error)
      }
    },
    initializeAttachmentUpload (item, type) {
      this.activeUploadItem = item
      this.activeUploadType = type
      this.$refs.relatedFileChooser.$refs.input.click()
    },
    async performFileUpload () {
      const name = `Anlage ${this.activeUploadType} - ${this.uploadFile.name}`
      await this.$store.dispatch('contracts/' + UPLOAD_CONTRACT_RELATED_FILE, {
        file: this.uploadFile,
        name,
        attachmentNumber: this.activeUploadType,
        customerUrl: this.activeUploadItem.url
      })

      this.resetUploadData()
    },
    resetUploadData () {
      this.activeUploadItem = null
      this.activeUploadType = null
      this.uploadFile = null
    }
  },
  watch: {
    uploadFile (newValue, oldValue) {
      if (!newValue || newValue === oldValue) {
        return
      }

      this.performFileUpload()
    }
  },
  async mounted () {
    await Promise.allSettled([
      this.$store.dispatch('organization/' + FETCH_ORGANIZATIONS),
      this.$store.dispatch('admin/' + FETCH_CONTACTS),
      this.$store.dispatch('device/' + FETCH_GLOBAL_DEVICES),
      this.$store.dispatch('contracts/' + FETCH_CONTRACTS),
      this.$store.dispatch('contracts/' + FETCH_CONTRACT_CUSTOMER_DATA),
      this.$store.dispatch('contracts/' + FETCH_CONTRACT_PERSON_DATA),
      this.$store.dispatch('contracts/' + FETCH_CONTRACT_RELATED_FILES),
      this.$store.dispatch('contracts/' + FETCH_GENERATED_CONTRACT_BUNDLES),
    ])
  },
}
</script>

<style lang="scss" scoped>
.grid-gap {
  grid-gap: 8px;
}

.related-devices {
  width: 40px;
}

.related-file-name {
  max-width: 200px;
}
</style>
